import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { StoreButtons } from "./StoreButtons";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SampleCard() {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.25], [0, -70]);

  return (
    <motion.div style={{ y }} className="relative mx-auto w-fit">
      <div className="absolute -inset-10 rounded-full bg-acid/15 blur-3xl" aria-hidden="true" />
      <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-hot/20 blur-2xl" aria-hidden="true" />
      <motion.div
        data-testid="sample-report-card"
        initial={{ opacity: 0, y: 48, rotate: 4 }}
        animate={{ opacity: 1, y: 0, rotate: -2 }}
        transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
        className="relative"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="w-[270px] rounded-2xl border border-olive bg-panel p-5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
        >
          <p className="text-center font-display text-base font-extrabold tracking-tight text-acid">DMWrapped</p>
          <p className="mt-2 text-center text-6xl leading-none">👻</p>
          <div className="mt-3 flex justify-center">
            <span data-testid="verdict-badge" className="rounded-full bg-hot px-4 py-1.5 font-display text-lg font-extrabold tracking-tight text-white">
              Ghost-in-Chief
            </span>
          </div>
          <div className="mt-3 rounded-lg border-[1.5px] border-acid px-3 py-2 text-center">
            <p className="text-sm font-bold text-paper">
              You reply faster than <span className="text-acid">92%</span> of texters
            </p>
          </div>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between" data-testid="top-person-stat">
              <span className="text-[13px] font-medium text-dim">Top person</span>
              <span className="text-[13px] font-bold text-paper">alex ✨</span>
            </div>
            <div className="flex items-center justify-between" data-testid="messages-count-stat">
              <span className="text-[13px] font-medium text-dim">Messages</span>
              <span className="text-[13px] font-bold text-paper">12,480</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-dim">You text first</span>
              <span className="text-[13px] font-bold text-paper">68%</span>
            </div>
          </div>
          <p className="mt-3 text-center text-xs font-semibold text-acid">dmwrapped.com</p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export function Hero({ onPreview }) {
  return (
    <section className="relative overflow-hidden px-4 pb-16 pt-32 sm:px-6 sm:pt-40 lg:px-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(247,247,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(247,247,248,0.04) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black 30%, transparent 75%)",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2 lg:gap-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-olive px-4 py-1.5"
          >
            <ShieldCheck size={14} className="text-acid" strokeWidth={2.5} />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-acid">
              100% on-device · Private by design
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-paper sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.2}>Your Instagram</MaskedLine>
            <MaskedLine delay={0.32}>
              DMs, <span className="text-acid">wrapped.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-dim"
          >
            Ever wondered who you <span className="text-paper font-semibold">actually</span> text the
            most? DMWrapped looks at your Instagram message history and shows you the fun stuff:
            your most-texted people, who usually texts first, how fast you reply — and a brutally
            honest verdict on your texting style. <span className="text-paper font-semibold">Free, and
            100% private:</span> it all happens on your device. Nothing is ever uploaded.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-8"
          >
            <StoreButtons idPrefix="hero" size="lg" />
            <button
              data-testid="hero-screenshots-link"
              onClick={onPreview}
              className="group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-dim transition-colors hover:text-acid"
            >
              or see what the app looks like
              <span className="transition-transform duration-300 group-hover:translate-y-0.5">↓</span>
            </button>
            <p className="mt-3 text-sm font-medium text-dim">No messages ever leave your device.</p>
          </motion.div>
        </div>

        <SampleCard />
      </div>
    </section>
  );
}
